<template>
  <!-- sign-off / add-note modal, shared by Coaching Tickets and the Coaching
       Board. a coaching record is a personnel record: the comment is
       required, the signer is captured, the trail is append-only, and
       nothing is editable once saved. parents open it with
       $refs.<ref>.open(ticket, { ack }) and reload on @changed. -->
  <b-modal id="cp-signoff-modal" size="lg" hide-footer
           :title="signTicket ? (signIsRecognition ? (signClosed ? 'Recognition record' : 'Recognition sign-off') : (signClosed ? 'Coaching record' : 'Coaching sign-off')) : ''"
           @hidden="onHidden">
    <div v-if="signTicket">
      <div class="cp-kv">
        <div class="cp-kv-k">Driver</div>
        <div><b>{{ signTicket.driver_name }}</b></div>
        <div class="cp-kv-k">Topic</div>
        <div>{{ signTicket.topic }}</div>
        <div class="cp-kv-k">Raised by</div>
        <div><b-badge :variant="sourceVariant(signTicket.source)">{{ sourceLabel(signTicket.source) }}</b-badge></div>
        <div class="cp-kv-k">Created</div>
        <div>
          {{ formatDateTime(signTicket.created_at) }}
          <span v-if="signTicket.created_by_name" class="cp-small text-muted">
            by {{ signTicket.created_by_name }}
          </span>
        </div>
        <div v-if="signTicket.why" class="cp-kv-k">Why</div>
        <div v-if="signTicket.why" class="cp-small">{{ signTicket.why }}</div>
        <div class="cp-kv-k">Assigned to</div>
        <div v-if="!signClosed" class="cp-reassign">
          <b-form-select v-model="reassignTo" :options="contactOptions" size="sm" class="cp-reassign-select" />
          <b-button v-if="reassignTo && reassignTo !== signTicket.assigned_to" size="sm"
                    variant="outline-primary" :disabled="reassigning" @click="reassignTicket">
            {{ reassigning ? 'Moving…' : 'Reassign' }}
          </b-button>
        </div>
        <div v-else>{{ signTicket.assigned_to_name || signTicket.assigned_to }}</div>
        <div class="cp-kv-k">Due</div>
        <div>
          {{ formatDate(signTicket.due_date) }}
          <b-badge v-if="signTicket.past_due" variant="danger" class="ml-1">past due</b-badge>
        </div>
        <div class="cp-kv-k">Status</div>
        <div>
          <b-badge :variant="statusVariant(signTicket.status)">{{ statusLabel(signTicket.status) }}</b-badge>
          <span v-if="signTicket.sign_off" class="cp-small text-muted ml-2">
            signed by {{ signTicket.sign_off.by_name }} on {{ formatDate(signTicket.sign_off.at) }}
          </span>
          <b-badge v-if="signTicket.acknowledged_in_person" variant="info" class="ml-1"
                   title="The driver acknowledged this conversation in person">
            acknowledged in person
          </b-badge>
        </div>
      </div>

      <template v-if="!signClosed">
        <div class="cp-label mt-3">Outcome</div>
        <div class="cp-pills">
          <button v-for="o in signOutcomeOptions" :key="o.value" type="button"
                  :class="['cp-chip', { 'cp-chip--active': signOutcome === o.value }]"
                  :aria-pressed="signOutcome === o.value ? 'true' : 'false'"
                  @click="signOutcome = o.value">{{ o.text }}</button>
        </div>
      </template>

      <div class="cp-label mt-3">
        {{ signClosed ? 'Add a note to the trail' : 'Comment' }}
        <span v-if="!signClosed" class="text-danger">— required</span>
      </div>
      <b-form-textarea v-model="signComment" rows="4"
        placeholder="What was discussed, what the driver said, and what happens next. This is the record that gets read if the decision is ever challenged." />
      <div class="cp-small mt-1" :class="signReady ? 'text-success' : 'text-muted'">
        {{ signReady ? '✓ ready to save' : (minCommentLength - signComment.trim().length) + ' more characters needed — at least a sentence, please' }}
      </div>

      <b-form-checkbox v-if="!signClosed && !signIsRecognition" v-model="signAck" class="mt-3">
        The driver acknowledged this in person
        <span class="cp-small text-muted">— recorded on the signature; it matters if the record is disputed</span>
      </b-form-checkbox>

      <b-alert show variant="secondary" class="cp-signing-as mt-3">
        <b>Signing as {{ identityName }}</b> — {{ roleLabel }}, {{ identityEmail }}.
        Your name, the timestamp and this comment are written to the coaching record.
        Entries cannot be edited once saved.
      </b-alert>

      <!-- written documentation: the warning letter, the signed
           acknowledgement, the retraining certificate. allowed on closed
           records too (the paperwork usually follows the conversation);
           removal is not, once signed. -->
      <div class="cp-attachments">
        <div class="cp-label">
          Documentation
          <span class="cp-hint">{{ attachments.length }}/{{ maxAttachments }}</span>
        </div>
        <div v-for="a in attachments" :key="a.attachment_id" class="cp-att-row">
          <i class="fa" :class="fileIcon(a.content_type)" aria-hidden="true"></i>
          <button type="button" class="cp-att-name" :title="'Open ' + a.name"
                  @click="openAttachment(a)">{{ a.name }}</button>
          <span class="cp-att-meta">{{ fileSize(a.byte_size) }} · {{ a.uploaded_by_name || a.uploaded_by }}</span>
          <button v-if="!signClosed" type="button" class="cp-att-del"
                  :aria-label="'Remove ' + a.name" :disabled="attachBusy"
                  @click="removeAttachment(a)"><i class="fa fa-times" aria-hidden="true"></i></button>
        </div>
        <div v-if="!attachments.length" class="cp-small text-muted">
          Nothing attached yet.
        </div>
        <div v-if="attachments.length < maxAttachments" class="mt-2">
          <input ref="attachInput" type="file" class="cp-att-input" multiple
                 :accept="acceptTypes" @change="onFilesPicked" />
          <b-button size="sm" variant="outline-secondary" :disabled="attachBusy"
                    @click="$refs.attachInput.click()">
            <i class="fa fa-paperclip"></i>&nbsp;{{ attachBusy ? 'Uploading…' : 'Attach a document' }}
          </b-button>
          <span class="cp-small text-muted ml-2">PDF, Word, or image · up to 20 MB</span>
        </div>
        <div v-if="signClosed && attachments.length" class="cp-small text-muted mt-1">
          Attachments on a signed record can't be removed — attach a correction instead.
        </div>
      </div>

      <div v-if="(signTicket.notes || []).length" class="cp-trail">
        <div class="cp-label">Trail <span class="cp-hint">{{ signTicket.notes.length }} entr{{ signTicket.notes.length > 1 ? 'ies' : 'y' }}</span></div>
        <div v-for="n in trailNotes" :key="n.note_id" class="cp-trail-entry">
          <div class="d-flex justify-content-between cp-small text-muted">
            <span><b>{{ n.by_name }}</b> · {{ n.by_role }}</span>
            <span>{{ formatDateTime(n.at) }}</span>
          </div>
          <div v-if="n.outcome" class="mt-1"><b-badge variant="light">{{ outcomeLabel(n.outcome) }}</b-badge></div>
          <div class="cp-small mt-1">{{ n.text }}</div>
        </div>
      </div>

      <div class="d-flex justify-content-between mt-3">
        <div>
          <b-button v-if="!signClosed && signTicket.status === 'open'" size="sm" variant="outline-secondary"
                    :disabled="signSaving" @click="markScheduled">Mark scheduled</b-button>
          <!-- for tickets raised in error: fleet admins only, open tickets
               only (a signed record is reopened first, never deleted) -->
          <b-button v-if="canDelete" size="sm" variant="outline-danger" class="ml-1"
                    :disabled="signSaving || deleting" @click="deleteTicket">
            {{ deleting ? 'Deleting…' : 'Delete ticket' }}
          </b-button>
        </div>
        <div>
          <b-button variant="outline-secondary" class="mr-2" @click="close">Cancel</b-button>
          <b-button variant="primary" :disabled="!signReady || signSaving" @click="saveSignOff">
            {{ signSaving ? 'Saving…' : (signClosed ? 'Add note' : '✓ Sign off as ' + identityFirstName) }}
          </b-button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'
import axios from 'axios'
import {
  get_contact_users, update_coaching_ticket,
  sign_off_coaching_ticket, add_coaching_ticket_note,
  presign_coaching_attachment, add_coaching_attachments,
  delete_coaching_attachment, get_coaching_attachment_url,
  delete_coaching_ticket,
} from '../networking/apis'
import {
  MIN_COMMENT_LENGTH, OPEN_STATUSES,
  statusLabel, statusVariant, sourceLabel, sourceVariant, outcomeLabel,
  OUTCOME_OPTIONS, RECOGNITION_OUTCOME_OPTIONS,
} from '../utils/coaching_labels'

export default {
  name: 'coaching_signoff_modal',
  props: {
    // the acting tenant, resolved by the parent page (resolveActingTenant) —
    // every write goes to this tenant, so parents MUST close() on tenant
    // change before the queue reloads (per-tenant ticket ids collide)
    tenant: { type: [String, Number], required: true },
  },
  data() {
    return {
      signTicket: null,
      signComment: '',
      signOutcome: 'delivered',
      signAck: false,
      signSaving: false,

      contacts: [],
      contactsLoaded: false,
      reassignTo: null,
      reassigning: false,
      deleting: false,

      // whether this open() session wrote anything — @hidden reports it so
      // the board can revert an optimistic drag when the user cancels
      mutated: false,

      attachBusy: false,
      maxAttachments: 10,
      // mirrors COACHING_CONTENT_TYPES server-side; the input filter is a
      // convenience, the API is the actual gate
      acceptTypes: '.pdf,.doc,.docx,.txt,image/jpeg,image/png,image/gif,image/webp',

      minCommentLength: MIN_COMMENT_LENGTH,
    }
  },
  computed: {
    ...mapState({ user: state => state.profile.user }),
    identityName() { return this.user.full_name || this.user.username },
    identityFirstName() { return (this.identityName || '').split(' ')[0] },
    identityEmail() { return this.user.username },
    roleLabel() {
      const labels = {
        admin: 'Tech Support', 'customer-admin': 'Fleet Admin',
        customer: 'Staff', manager: 'Manager',
      }
      return labels[this.user.role] || this.user.role
    },
    signIsRecognition() {
      return !!this.signTicket && this.signTicket.kind === 'recognition'
    },
    signOutcomeOptions() {
      return this.signIsRecognition ? RECOGNITION_OUTCOME_OPTIONS : OUTCOME_OPTIONS
    },
    signClosed() {
      return !!this.signTicket && !OPEN_STATUSES.includes(this.signTicket.status)
    },
    signReady() {
      return this.signComment.trim().length >= MIN_COMMENT_LENGTH
    },
    attachments() {
      return (this.signTicket && this.signTicket.attachments) || []
    },
    trailNotes() {
      return [...(this.signTicket.notes || [])].reverse()
    },
    // delete is fleet-admins-only (mirrors the server's erm-admin gate) and
    // open tickets only. FI-1647: a muggle-view admin sees the customer
    // experience, and here the fleet-admin trio keeps the button anyway —
    // the effective role stays inside the gate either way.
    canDelete() {
      if (!this.signTicket || this.signClosed) return false
      const viewAs = this.$store.state.profile.view_as
      const role = this.user.role === 'admin' && viewAs === 'non-admin' ? 'customer-admin' : this.user.role
      return ['admin', 'customer-admin'].includes(role)
    },
    contactOptions() {
      const options = this.contacts.map(c => ({
        value: c.username,
        text: `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.username,
      }))
      // the current assignee stays selectable even if the contacts feed
      // doesn't carry them (departed user, api-created ticket)
      if (this.signTicket && !options.some(o => o.value === this.signTicket.assigned_to)) {
        options.unshift({
          value: this.signTicket.assigned_to,
          text: this.signTicket.assigned_to_name || this.signTicket.assigned_to,
        })
      }
      return options
    },
  },
  methods: {
    statusLabel, statusVariant, sourceLabel, sourceVariant, outcomeLabel,
    formatDate(d) { return d ? moment(d.slice(0, 10)).format('MMM D, YYYY') : '—' },
    formatDateTime(d) { return d ? moment(d).format('MMM D, YYYY h:mm a') : '—' },
    apiErrorMessage(e, fallback) {
      return (e && e.response && e.response.data && e.response.data.message) || fallback
    },
    toast(message, variant, delay) {
      this.$bvToast.toast(message, { title: 'Coaching Portal', variant, autoHideDelay: delay })
    },

    // ack: preselect "acknowledged in person" for callers that already
    // know it happened
    open(ticket, { ack = false } = {}) {
      this.signTicket = ticket
      this.signComment = ''
      this.signOutcome = ticket.kind === 'recognition' ? 'recognized_in_person' : 'delivered'
      this.signAck = ack && ticket.kind !== 'recognition'
      this.reassignTo = ticket.assigned_to
      this.mutated = false
      if (OPEN_STATUSES.includes(ticket.status) && !this.contactsLoaded) this.loadContacts()
      this.$bvModal.show('cp-signoff-modal')
    },
    close() {
      this.$bvModal.hide('cp-signoff-modal')
    },
    // tenant changed under us: drop the contacts cache along with the ticket
    resetTenantScope() {
      this.close()
      this.signTicket = null
      this.contacts = []
      this.contactsLoaded = false
      this.reassignTo = null
    },
    onHidden() {
      this.$emit('hidden', { mutated: this.mutated })
    },

    fileIcon(type) {
      if ((type || '').startsWith('image/')) return 'fa-file-image-o'
      if (type === 'application/pdf') return 'fa-file-pdf-o'
      if ((type || '').includes('word')) return 'fa-file-word-o'
      return 'fa-file-text-o'
    },
    fileSize(bytes) {
      if (!bytes) return '—'
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    },
    // presign -> PUT straight to s3 -> finalize onto the ticket. the direct
    // upload is what dodges the api-gateway 6 MB body cap (same trick the
    // erm photo flow uses).
    async onFilesPicked(event) {
      const files = [...(event.target.files || [])]
      event.target.value = ''
      if (!files.length) return
      const room = this.maxAttachments - this.attachments.length
      if (files.length > room) {
        this.toast(`Only ${room} more file${room === 1 ? '' : 's'} can be attached to this ticket.`, 'warning', 5000)
      }
      this.attachBusy = true
      const keys = []
      try {
        for (const file of files.slice(0, room)) {
          const resp = await presign_coaching_attachment(this.tenant, {
            filename: file.name,
            content_type: file.type,
            byte_size: file.size,
          })
          const presigned = (resp.value && resp.value.data) || {}
          const form = new FormData()
          Object.entries(presigned.upload.fields).forEach(([k, v]) => form.append(k, v))
          form.append('file', file)
          await axios.post(presigned.upload.url, form)
          keys.push(presigned.key)
        }
        if (!keys.length) return
        const saved = await add_coaching_attachments(this.tenant, this.signTicket.id, keys)
        const updated = (saved.value && saved.value.data) || null
        if (updated) this.signTicket = updated
        this.mutated = true
        this.toast(`${keys.length} document${keys.length === 1 ? '' : 's'} attached to the record.`, 'success', 4000)
        this.$emit('changed')
      } catch (e) {
        this.toast(this.apiErrorMessage(e, 'Could not attach the file.'), 'danger', 6000)
      } finally {
        this.attachBusy = false
      }
    },
    async openAttachment(attachment) {
      try {
        const resp = await get_coaching_attachment_url(
          this.tenant, this.signTicket.id, attachment.attachment_id)
        const url = ((resp.value && resp.value.data) || {}).url
        if (url) window.open(url, '_blank', 'noopener')
      } catch (e) {
        this.toast(this.apiErrorMessage(e, 'Could not open that document.'), 'danger', 6000)
      }
    },
    async removeAttachment(attachment) {
      const ok = await this.$bvModal.msgBoxConfirm(
        `Remove "${attachment.name}" from this coaching record?`,
        { title: 'Remove document', okTitle: 'Remove', okVariant: 'danger', size: 'sm' },
      )
      if (!ok) return
      this.attachBusy = true
      try {
        const resp = await delete_coaching_attachment(
          this.tenant, this.signTicket.id, attachment.attachment_id)
        const updated = (resp.value && resp.value.data) || null
        if (updated) this.signTicket = updated
        this.mutated = true
        this.$emit('changed')
      } catch (e) {
        this.toast(this.apiErrorMessage(e, 'Could not remove the document.'), 'danger', 6000)
      } finally {
        this.attachBusy = false
      }
    },

    async loadContacts() {
      this.contactsLoaded = true
      try {
        const resp = await get_contact_users(this.tenant)
        const payload = (resp.value && resp.value.data) || {}
        this.contacts = payload.results || []
      } catch (e) {
        // the picker degrades to the current assignee; retry on next open
        this.contactsLoaded = false
      }
    },
    async reassignTicket() {
      const target = this.contactOptions.find(o => o.value === this.reassignTo)
      if (!target) return
      this.reassigning = true
      try {
        const resp = await update_coaching_ticket(this.tenant, this.signTicket.id, {
          assigned_to: target.value,
          assigned_to_name: target.text,
        })
        const updated = (resp.value && resp.value.data) || null
        if (updated) this.signTicket = updated
        this.mutated = true
        this.toast(`Reassigned to ${target.text} — they'll see it in their queue and get a notification.`, 'success', 4000)
        this.$emit('changed')
      } catch (e) {
        this.toast(this.apiErrorMessage(e, 'Could not reassign the ticket.'), 'danger', 6000)
      } finally {
        this.reassigning = false
      }
    },
    async deleteTicket() {
      const t = this.signTicket
      const ok = await this.$bvModal.msgBoxConfirm(
        `Permanently delete ticket #${t.id} — ${t.driver_name}, ${t.topic}? `
        + 'The ticket, its trail, and its attachments are removed for good. '
        + 'If the coaching actually happened, sign it off instead.',
        { title: 'Delete this ticket?', okTitle: 'Delete ticket', okVariant: 'danger' },
      )
      if (!ok) return
      this.deleting = true
      try {
        await delete_coaching_ticket(this.tenant, t.id)
        this.mutated = true
        this.close()
        this.toast(`Ticket #${t.id} deleted.`, 'warning', 4000)
        this.$emit('changed')
      } catch (e) {
        this.toast(this.apiErrorMessage(e, 'Could not delete the ticket.'), 'danger', 6000)
      } finally {
        this.deleting = false
      }
    },
    async markScheduled() {
      this.signSaving = true
      try {
        const resp = await update_coaching_ticket(this.tenant, this.signTicket.id, {
          status: 'scheduled',
          scheduled_for: moment().add(7, 'days').format('YYYY-MM-DD'),
        })
        const updated = (resp.value && resp.value.data) || null
        this.mutated = true
        this.close()
        this.toast(
          `Marked scheduled for ${updated && updated.scheduled_for ? this.formatDate(updated.scheduled_for) : 'next week'}.`,
          'info', 4000,
        )
        this.$emit('changed')
      } catch (e) {
        this.toast(this.apiErrorMessage(e, 'Could not update the ticket.'), 'danger', 6000)
      } finally {
        this.signSaving = false
      }
    },
    async saveSignOff() {
      this.signSaving = true
      try {
        if (this.signClosed) {
          await add_coaching_ticket_note(this.tenant, this.signTicket.id, {
            text: this.signComment.trim(),
          })
          this.toast('Note added to the coaching record.', 'info', 4000)
        } else {
          await sign_off_coaching_ticket(this.tenant, this.signTicket.id, {
            comment: this.signComment.trim(),
            outcome: this.signOutcome,
            acknowledged_in_person: this.signAck,
          })
          this.toast(`Signed off by ${this.identityName}. The comment is on the coaching record.`, 'success', 5000)
        }
        this.mutated = true
        this.close()
        this.$emit('changed')
      } catch (e) {
        this.toast(this.apiErrorMessage(e, 'Could not save — nothing was written.'), 'danger', 6000)
      } finally {
        this.signSaving = false
      }
    },
  },
}
</script>

<style scoped>
.cp-small { font-size: 0.85em; }
.cp-hint { font-weight: 400; font-size: 0.85em; opacity: 0.7; }
.cp-kv { display: grid; grid-template-columns: 110px 1fr; row-gap: 6px; column-gap: 10px; }
.cp-kv-k {
  font-size: 0.75em; text-transform: uppercase; letter-spacing: 0.04em;
  opacity: 0.65; align-self: center;
}
.cp-label {
  font-size: 0.8em; font-weight: 600; text-transform: uppercase;
  letter-spacing: 0.04em; margin-bottom: 5px;
}
.cp-pills { display: flex; flex-wrap: wrap; gap: 6px; }
.cp-signing-as { font-size: 0.9em; }
.cp-reassign { display: flex; align-items: center; gap: 6px; }
.cp-reassign-select { max-width: 240px; }
/* pill chip recipe mirrored from the scorecard toolbar (its definition is
   scoped to that component, so the modal carries its own copy) */
.cp-chip {
  background: var(--fi-surface, #f8fafc);
  border: 1px solid var(--fi-line, #d6dde6);
  border-radius: var(--fi-radius-pill, 999px);
  padding: 4px 12px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--fi-text-muted, #4b5563);
  cursor: pointer;
  transition: background .12s, border-color .12s, color .12s;
}
/* the active chip keeps its own fill on hover (see erm_board) */
.cp-chip:hover:not(.cp-chip--active) { background: var(--fi-surface-sunken, #eef4fb); }
.cp-chip--active {
  background: var(--fi-ops-accent, #3a6e99);
  border-color: var(--fi-ops-accent, #3a6e99);
  color: #fff;
}
.cp-attachments { margin-top: 14px; }
.cp-att-input { display: none; }
.cp-att-row {
  display: flex; align-items: center; gap: 8px;
  padding: 4px 0;
  border-bottom: 1px solid var(--fi-line, #eee);
  font-size: 0.9em;
}
.cp-att-row .fa { color: var(--fi-text-muted, #6b7280); }
.cp-att-name {
  background: none; border: none; padding: 0;
  color: var(--fi-ops-accent-strong, #2d5a80); text-decoration: underline;
  cursor: pointer; text-align: left;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.cp-att-meta { margin-left: auto; font-size: 0.85em; color: var(--fi-text-muted, #6b7280); white-space: nowrap; }
.cp-att-del {
  background: none; border: none; padding: 2px 4px;
  color: #9aa3ad; cursor: pointer;
}
.cp-att-del:hover { color: #b91c1c; }
.cp-trail { margin-top: 14px; }
.cp-trail-entry {
  padding: 8px 10px; border-bottom: 1px solid var(--fi-line, #eee);
  background: var(--fi-surface-sunken, #f8f9fa); border-radius: 4px; margin-bottom: 6px;
}
</style>
